/**
 * Step 1 of sign-in: choose your school.
 *
 * Visual parity with the shipped screen: dark full-bleed shell, white card,
 * "Dalan / Plateforme scolaire" wordmark, French copy.
 *
 * The lookup is debounced and needs 3 characters, because the endpoint is
 * rate-limited per IP specifically to stop prefix-sweeping the school
 * catalogue — firing on every keystroke would burn a real user's budget.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { lookupSchools, rememberSchool, type School } from "@/features/auth/session";
import { messageFor } from "@/api/client";
import { useScopedState } from "@/lib/scoped-state";

const MIN_QUERY = 3;
const DEBOUNCE_MS = 250;
/** Stable identity so the scoped state does not reset on every render. */
const NO_SCHOOLS: School[] = [];

export function SchoolSelect({ onPick }: { onPick: (school: School) => void }) {
  const [query, setQuery] = useState("");
  // Results are scoped to the term that produced them. Clearing them in an
  // effect when the box is emptied would leave one render where the PREVIOUS
  // school's results sit under the new query — on a login screen that picks
  // which school you are signing into, that render is a mis-click waiting.
  const term = query.trim();
  const [found, setFound] = useScopedState<School[]>(term, NO_SCHOOLS);
  const [state, setState] = useScopedState<"idle" | "searching" | "error">(term, "idle");
  const [error, setError] = useScopedState<string | null>(term, null);
  const results = term.length < MIN_QUERY ? NO_SCHOOLS : found;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (term.length < MIN_QUERY) return; // nothing to look up; scoped state is already empty
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setState("searching");
      setError(null);
      try {
        setFound(await lookupSchools(term, controller.signal));
        setState("idle");
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(messageFor(cause));
        setState("error");
      }
    }, DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [term, setFound, setState, setError]);

  const emptyMessage = useMemo(() => {
    if (state === "searching") return "Recherche…";
    if (query.trim().length < MIN_QUERY) return "Commencez à taper le nom de votre école ci-dessus.";
    return "Aucune école trouvée. Vérifiez l'orthographe ou contactez votre administration.";
  }, [state, query]);

  return (
    <div className="min-h-screen bg-chrome flex items-center justify-center p-4">
      <div className="w-full max-w-[480px] rounded-lg bg-surface px-8 py-9 shadow-2xl">
        <h1 className="text-3xl font-extrabold tracking-tight text-text-strong">Dalan</h1>
        <p className="mt-1 text-sm text-text-muted">Plateforme scolaire</p>

        <h2 className="mt-6 text-lg font-bold text-text-strong">Sélectionnez votre école</h2>
        <p className="mt-1 mb-4 text-sm text-text-muted">
          Choisissez d'abord votre établissement, puis connectez-vous.
        </p>

        <input
          ref={inputRef}
          className="field"
          type="text"
          autoComplete="off"
          placeholder="Tapez le nom de votre école…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Nom de l'école"
        />

        <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-text-muted">
          Écoles disponibles
        </div>

        {error && (
          <p className="mt-3 rounded border-l-4 border-danger bg-danger-soft px-3 py-2 text-sm text-danger-dark">
            {error}
          </p>
        )}

        <div className="mt-2" role="listbox" aria-label="Écoles">
          {results.length === 0 ? (
            <p className="py-3 text-sm text-text-muted">{emptyMessage}</p>
          ) : (
            results.slice(0, 8).map((school) => (
              <button
                key={school.org_id}
                type="button"
                role="option"
                aria-selected={false}
                className="flex w-full items-center justify-between rounded border border-transparent px-3 py-3 text-left hover:border-border hover:bg-surface-muted"
                onClick={() => {
                  rememberSchool(school);
                  onPick(school);
                }}
              >
                <span>
                  <span className="block font-semibold text-text-strong">{school.name}</span>
                  <span className="block text-xs text-text-muted">
                    {[school.code, school.country].filter(Boolean).join(" · ") || "École"}
                  </span>
                </span>
                <span aria-hidden className="text-text-faint">›</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
